import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  IconButton,
  InputBase,
  Typography,
  Tooltip,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Search, Add } from "@mui/icons-material";
import { useToast } from "../../services/utils";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import useFetchGroupById from "../../hooks/useFetchGroupById";
import useFetchUsersByGroupId from "../../hooks/useFetchUsersByGroupId";
import { assignUserToGroup } from "../../services/groupService";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import ButtonComponent from "../../components/Button.Component";
import Loader from "../../components/Molecules/Loader/Loader";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { AddUserToGroupModal } from "../../components/Groups";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import useFetchUsers from "../../hooks/useFetchUsers";
import { IconDeleteBlueSvg } from "../../common/utils/icons";
import { useAuth } from "../../contexts/AuthContext";

interface User {
  id: number;
  name?: string;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  user_id?: number;
}

const AssignUsers: React.FC = () => {
  const { id: groupId } = useParams();
  const { showToast } = useToast();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { hasPermission } = useAuth();

  const canEditUserGroup = hasPermission("user_group_users", "edit");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [userToRemove, setUserToRemove] = useState<User | null>(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState<boolean>(false);
  const [assignedUserIds, setAssignedUserIds] = useState<number[]>([]);
  const [assigneduserData, setAssignedUserData] = useState<any>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] =
    useState<boolean>(false);

  const [allUsers] = useFetchUsers({
    setIsLoading,
  });

  // Fetch group data
  const [groupData] = useFetchGroupById({
    setIsLoading,
    groupId: groupId || null,
  });

  // Fetch assigned users
  const [assignedUsers, refetchUsers] = useFetchUsersByGroupId({
    setIsLoading,
    groupId: groupId || null,
  }) as [any[], () => Promise<void>];

  // Set breadcrumb when group data is loaded
  useEffect(() => {
    if (groupData) {
      setCurrentBreadcrumbPage({
        name: groupData?.name,
        id: groupData?.id,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [groupData, setCurrentBreadcrumbPage]);

  useEffect(() => {
    if (assignedUsers?.length >= 0) {
      setAssignedUserIds(assignedUsers.map((user: User) => user?.user_id || 0));
    }
  }, [assignedUsers]);

  useEffect(() => {
    if (assignedUserIds?.length >= 0 && allUsers?.length >= 0) {
      const users = allUsers.filter((user: User) =>
        assignedUserIds.includes(user.id)
      );
      setAssignedUserData(users);
    }
  }, [assignedUserIds, allUsers]);

  // Helper function to get user display name
  const getUserDisplayName = (user: User) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.username) return user.username;
    if (user?.email) return user.email;
    return "Unknown User";
  };

  // Handle remove user
  const handleRemoveUser = (user: User) => {
    setUserToRemove(user);
    setDeleteConfirmOpen(true);
  };

  // Confirm remove user
  const confirmRemoveUser = async () => {
    if (!userToRemove || !groupId) return;

    try {
      setIsLoading(true);
      const userId = userToRemove.user_id || userToRemove.id;
      const payload: any = {
        group_id: Number(groupId),
        user_ids:
          assignedUserIds && assignedUserIds.filter((id) => id !== userId),
      };
      await assignUserToGroup(payload);
      showToast("User removed from group successfully", "success");
      setDeleteConfirmOpen(false);
      setUserToRemove(null);
      refetchUsers();
    } catch (error: any) {
      console.error("Error removing user from group:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk remove users
  const handleBulkRemoveUsers = () => {
    if (selectedUserIds.length === 0) {
      showToast("Please select users to remove", "warning");
      return;
    }
    setBulkRemoveConfirmOpen(true);
  };

  // Confirm bulk remove users
  const confirmBulkRemoveUsers = async () => {
    if (!groupId || selectedUserIds.length === 0) return;

    try {
      setIsLoading(true);
      const payload: any = {
        group_id: Number(groupId),
        user_ids: assignedUserIds.filter((id) => !selectedUserIds.includes(id)),
      };
      const result = await assignUserToGroup(payload);

      if (result.removed_user_ids > 0) {
        showToast(
          `${result.removed_user_ids} user(s) removed successfully`,
          "success"
        );
      }

      setBulkRemoveConfirmOpen(false);
      setSelectedUserIds([]);
      refetchUsers();
    } catch (error: any) {
    } finally {
      setIsLoading(false);
    }
  };

  // Handle selection change
  const handleSelectionChange = (newSelection: number[]) => {
    setSelectedUserIds(newSelection);
  };

  // Handle add user modal success
  const handleAddUserSuccess = () => {
    refetchUsers();
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!assigneduserData) return [];

    if (!searchTerm.trim()) {
      return assigneduserData;
    }

    return assigneduserData.filter((user: User) => {
      const displayName = getUserDisplayName(user).toLowerCase();
      const email = user.email?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();
      return displayName.includes(searchLower) || email.includes(searchLower);
    });
  }, [assigneduserData, searchTerm]);

  // Define columns for the data grid
  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: "User ID",
      width: 100,
      sortable: false,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => (
        <>
          <Box className="main-header no-bg">
            <Typography className="username">
              {params.row.firstname ? params.row.firstname.slice(0, 1) : ""}
              {params.row.lastname ? params.row.lastname.slice(0, 1) : ""}
            </Typography>
          </Box>
          &nbsp;
          {params.row.firstname} {params.row.lastname}
        </>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.email || "No email"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params: any) =>
        canEditUserGroup && (
          <Tooltip title="Remove User" placement="top" arrow>
            <IconButton
              className="datagrid-action-btn"
              color="inherit"
              onClick={() => handleRemoveUser(params.row)}
            >
              <IconDeleteBlueSvg />
            </IconButton>
          </Tooltip>
        ),
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />

      {/* Confirm Remove Dialog */}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmRemoveUser}
        dailogTitle="Remove User"
        dailogDescription={`Are you sure you want to remove "${
          userToRemove ? getUserDisplayName(userToRemove) : "this user"
        }" from group "${groupData?.name}"?`}
      />

      {/* Confirm Bulk Remove Dialog */}
      <ConfirmDailog
        isDailogOpen={bulkRemoveConfirmOpen}
        handleCancelAction={() => setBulkRemoveConfirmOpen(false)}
        handleConfirmAction={confirmBulkRemoveUsers}
        dailogTitle="Remove Selected Users"
        dailogDescription={`Are you sure you want to remove ${selectedUserIds.length} selected user(s) from group "${groupData?.name}"?`}
      />

      {/* Search Section */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        className="text-box-card compact-text-box-card"
      >
        <Grid item>
          <label className="label-text">Search members</label>
          <form className="common-search-panel">
            <InputBase
              placeholder="Search by User Name"
              className="search-textbox"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }}
            />
            <IconButton className="search-icon">
              <Search className="svg_icons" />
            </IconButton>
          </form>
        </Grid>
        {canEditUserGroup && (
          <Grid item xs="auto">
            <label className="label-text">&nbsp;</label>
            <FlexBetween sx={{ columnGap: 2 }}>
              {selectedUserIds.length > 0 && (
                <ButtonComponent
                  handelClickEvent={handleBulkRemoveUsers}
                  className="btn-orange"
                >
                  Remove Selected ({selectedUserIds.length})
                </ButtonComponent>
              )}
              <ButtonComponent
                handelClickEvent={() => setAddUserModalOpen(true)}
                className="btn-orange"
              >
                <>
                  <Add sx={{ marginRight: "4px" }} /> Add
                </>
              </ButtonComponent>
            </FlexBetween>
          </Grid>
        )}
      </Grid>

      {/* Users Table */}
      <FlexBetween>
        <DataTable
          dataColumns={columns}
          dataRows={filteredUsers || []}
          loading={isLoading}
          dataListTitle="Assigned Users"
          className="dataTable no-radius hide-progress-icon"
          singlePageMaxHeightDiff={358}
          checkboxSelection={true}
          rowSelectionModel={selectedUserIds}
          onRowSelectionModelChange={handleSelectionChange}
        />
      </FlexBetween>

      {/* Add User Modal */}
      {groupData && (
        <AddUserToGroupModal
          allUsers={allUsers}
          open={addUserModalOpen}
          onClose={() => setAddUserModalOpen(false)}
          groupId={groupId!}
          groupName={groupData.name}
          onSuccess={handleAddUserSuccess}
          assignedUserIds={
            assignedUsers?.map((user: User) => user.user_id || user.id) || []
          }
        />
      )}
    </Box>
  );
};

export default AssignUsers;
